import { useEffect, useState } from "react";
import useAuth from "../hook/useAuth";
import { ContentState, convertToRaw, EditorState } from "draft-js";
import htmlToDraft from "html-to-draftjs";
import { Editor } from "react-draft-wysiwyg";
import { PleasewaitPopup } from "./PleasewaitPopup";
import '/node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import draftToHtml from "draftjs-to-html";
import { put_SubLessonContent } from "../services/SubLesson";

type EditSubLessonContentProps = {
    showPopup: boolean;
    setShowPopup: (value: boolean) => void;
    setRefreshContentList: (value: boolean) => void;
    data: any;
}

export const EditSubLessonContent = ({ showPopup, setShowPopup, setRefreshContentList, data }: EditSubLessonContentProps) => {
    const { auth }: any = useAuth();
    const [contentTitle, setContentTitle] = useState<string>("");
    const [editorState, setEditorState] = useState(() =>
        EditorState.createEmpty()
    );
    const [showPleasewait, setShowPleasewait] = useState<boolean>(false);

    useEffect(() => {
        if (showPopup) {
            document.body.classList.add("overflow-hidden");
        } else {
            document.body.classList.remove("overflow-hidden");
        }

        return () => {
            document.body.classList.remove("overflow-hidden");
        };
    }, [showPopup]);

    useEffect(() => {
        if (data) {
            setContentTitle(data?.title);
            let html: string = data?.lessonContent;
            html = html.replace(
                /<div\s+style="text-align:(left|none|right);?">\s*<img([^>]*)style="([^"]*)"([^>]*)>\s*<\/div>/g,
                (match, align, beforeStyle, imgStyle, afterStyle) => {
                    // Append alignment to existing img style
                    const newStyle = `${imgStyle};text-align:${align};`;
                    return `<img ${beforeStyle} style="${newStyle}" ${afterStyle}>`;
                }
            );

            // Images inside div with text-align but no existing img style
            html = html.replace(
                /<div\s+style="text-align:(left|none|right);?">\s*<img([^>]*)>\s*<\/div>/g,
                (match, align, rest) => {
                    return `<img style="text-align:${align};" ${rest}>`;
                }
            );

            const blocksFromHtml = htmlToDraft(html);
            if (blocksFromHtml) {
                const { contentBlocks, entityMap } = blocksFromHtml;
                const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
                setEditorState(EditorState.createWithContent(contentState));
            }
        }
    }, [data])


    if (!showPopup) return null;







    const handleOnSave = () => {
        const rawContentState = convertToRaw(editorState.getCurrentContent());
        let htmlContent = draftToHtml(rawContentState);
        htmlContent = htmlContent.replace(
            /<span class="rdw-image-alignment rdw-image-center">\s*<span class="rdw-image-imagewrapper">(.*?)<\/span>\s*<\/span>/g,
            '<div style="text-align:none;">$1</div>'
        );

        if (data) {
            setShowPleasewait(true);
            put_SubLessonContent({
                ...data,
                title: contentTitle,
                lessonContent: htmlContent,
                updatedAt: new Date().toLocaleString(),
                updatedBy: auth?.givenname,
            }, auth?.id)
                .then((res) => {
                    setShowPleasewait(false);
                    setShowPopup(false);
                    setRefreshContentList(true);
                })
                .catch((error) => {
                    console.error(error)
                })
        }
    }


    return (
        <>
            <div className="fixed inset-0 z-[9999999] flex items-center justify-center bg-black/50 p-10">
                <div className="bg-white w-full max-w-4xl rounded-md shadow-lg max-h-[90vh] flex flex-col overflow-auto">

                    <div className="px-6 py-4 border-b w-full flex justify-between items-center">
                        <h2 className="text-lg font-medium">Edit content</h2>
                        <button
                            onClick={() => {
                                setShowPopup(false)
                            }}
                            className="text-gray-500"
                        >
                            <span className="material-symbols-outlined text-2xl">
                                close
                            </span>
                        </button>
                    </div>


                    <div>
                        <div className="px-6 pt-6 space-y-4">

                            <label className="font-medium">Content Title</label>
                            <input
                                type="text"
                                placeholder="Title"
                                className="w-full mt-2 border rounded px-3 py-2 text-sm focus:outline-none focus:ring focus:ring-primary"
                                value={contentTitle}
                                onChange={(e) => setContentTitle(e.target.value)}
                            />

                        </div>

                        <div className="px-6 py-6 space-y-4">
                            <label className="font-medium">Content</label>
                            <div className="w-full">
                                <Editor
                                    editorStyle={{
                                        border: "1px solid #D0D0D0",
                                        backgroundColor: "white",
                                        minHeight: "150px",
                                        padding: "8px",
                                    }}
                                    editorState={editorState}
                                    wrapperClassName="wrapper-class"
                                    editorClassName="editor-class"
                                    toolbarClassName="toolbar-class"
                                    onEditorStateChange={setEditorState}
                                    toolbar={{
                                        options: [
                                            "inline",
                                            "blockType",
                                            "fontSize",
                                            "fontFamily",
                                            "list",
                                            "textAlign",
                                            "colorPicker",
                                            "link",
                                            "embedded",
                                            "emoji",
                                            "image",
                                            "remove",
                                            "history",
                                        ],
                                        inline: {
                                            inDropdown: false,
                                            options: [
                                                "bold",
                                                "italic",
                                                "underline",
                                                "strikethrough",
                                                "monospace",
                                                "superscript",
                                                "subscript",
                                            ],
                                        },
                                        blockType: {
                                            inDropdown: true,
                                            options: [
                                                "Normal",
                                                "H1",
                                                "H2",
                                                "H3",
                                                "H4",
                                                "H5",
                                                "H6",
                                                "Blockquote",
                                                "Code",
                                            ],
                                        },
                                        fontSize: {
                                            options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                                        },
                                        fontFamily: {
                                            options: [
                                                "Arial",
                                                "Georgia",
                                                "Impact",
                                                "Tahoma",
                                                "Times New Roman",
                                                "Verdana",
                                                "Courier New",
                                                "Roboto",
                                            ],
                                        },
                                        list: {
                                            inDropdown: false,
                                            options: ["unordered", "ordered", "indent", "outdent"],
                                        },
                                        textAlign: {
                                            inDropdown: false,
                                            options: ["left", "center", "right", "justify"],
                                        },
                                        colorPicker: {
                                            colors: [
                                                "rgb(97,189,109)",
                                                "rgb(26,188,156)",
                                                "rgb(84,172,210)",
                                                "rgb(44,130,201)",
                                                "rgb(147,101,184)",
                                                "rgb(71,85,119)",
                                                "rgb(204,204,204)",
                                                "rgb(65,168,95)",
                                                "rgb(0,168,133)",
                                                "rgb(61,142,185)",
                                                "rgb(41,105,176)",
                                                "rgb(85,57,130)",
                                                "rgb(40,50,78)",
                                                "rgb(0,0,0)",
                                                "rgb(250,197,28)",
                                                "rgb(243,121,52)",
                                                "rgb(209,72,65)",
                                                "rgb(184,49,47)",
                                                "rgb(247,218,100)",
                                                "rgb(251,160,38)",
                                                "rgb(235,107,86)",
                                                "rgb(226,80,65)",
                                            ],
                                        },
                                        link: {
                                            inDropdown: false,
                                            options: ["link", "unlink"],
                                        },
                                        embedded: {
                                            defaultSize: {
                                                height: "auto",
                                                width: "auto",
                                            },
                                        },
                                        emoji: {},
                                        image: {
                                            urlEnabled: true,
                                            uploadEnabled: true,
                                            alignmentEnabled: true,
                                            previewImage: true,
                                            inputAccept: "image/gif,image/jpeg,image/jpg,image/png,image/svg",
                                            alt: { present: true, mandatory: false },
                                        },
                                        remove: { className: undefined },
                                        history: {
                                            inDropdown: false,
                                            options: ["undo", "redo"],
                                        },
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="px-6 py-4 border-t flex justify-end space-x-3">

                        <button
                            className={`px-6 py-2 ${!contentTitle ? "bg-gray-300 text-white cursor-not-allowed" : "bg-primary text-white cursor-pointer"}  font-medium rounded`}
                            disabled={!contentTitle ? true : false}
                            onClick={handleOnSave}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div >
            <PleasewaitPopup showPopup={showPleasewait} />
        </>
    )
}